import Flex from 'components/ui/Flex';
import ItemCountDisplay from 'components/ui/ItemCountDisplay';
import Text from 'components/ui/Text';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import Spinner from 'components/ui/Spinner';

export type BarcodeAddedOrder = {
    orderNumber: string;
    customerCode: string;
    orderDate: string;
};

type BarcodeSearchModeContentProps = {
    statusMessage?: string | null;
    statusIcon?: IconDefinition | null;
    emptyStateText: string;
    barcodeAddedOrders?: BarcodeAddedOrder[];
    isLoading?: boolean;
};

const BarcodeSearchModeContent = ({
    statusMessage,
    statusIcon,
    emptyStateText,
    barcodeAddedOrders,
    isLoading = false,
}: BarcodeSearchModeContentProps) => {
    const { t } = useTranslation();

    const hasAddedOrders = (barcodeAddedOrders?.length ?? 0) > 0;
    const displayStatusMessage = isLoading ? null : statusMessage;
    const hasStatusMessage = !!displayStatusMessage;
    const showEmptyState = !isLoading && !hasStatusMessage && !hasAddedOrders;

    return (
        <BarcodeContentWrapper column gap="0.75rem" $grow={1}>
            <Flex column align="center">
                {isLoading && (
                    <Flex className="mt-3" column gap="1rem" align="center">
                        <Spinner className="mt-3" center size="small" color="text" />
                    </Flex>
                )}

                {hasStatusMessage ? (
                    <StatusMessageRow align="center" gap="0.5rem">
                        {statusIcon && <FontAwesomeIcon icon={statusIcon} />}
                        <Text margin={0} fontSize={14} color="text">
                            {displayStatusMessage}
                        </Text>
                    </StatusMessageRow>
                ) : showEmptyState ? (
                    <Flex column align="center" className="mt-1">
                        {statusIcon && <FontAwesomeIcon className="mt-3" icon={statusIcon} size="2x" />}
                        <Text className="mt-3" fontSize={20} thin as="label">
                            {emptyStateText}
                        </Text>
                    </Flex>
                ) : null}
            </Flex>

            {hasAddedOrders ? (
                <BarcodeListWrapper column gap="0.5rem" $grow={1}>
                    <ItemCountDisplay
                        title={t('orders')}
                        count={barcodeAddedOrders?.length ?? 0}
                        countLabel={t('orders')}
                    />
                    {(barcodeAddedOrders ?? []).map((order) => (
                        <BarcodeListItem key={order.orderNumber} justify="between" align="center">
                            <Flex column>
                                <Text margin={0} fontSize={16}>
                                    {order.orderNumber}
                                </Text>
                                <Text margin={0} fontSize={12} color="textFaded">
                                    {t('customerCode')}: {order.customerCode}
                                </Text>
                                <Text margin={0} fontSize={12} color="textFaded">
                                    {t('orderDateTime')}: {order.orderDate}
                                </Text>
                            </Flex>
                            <AddedBadge>{t('added', 'Added')}</AddedBadge>
                        </BarcodeListItem>
                    ))}
                </BarcodeListWrapper>
            ) : null}
        </BarcodeContentWrapper>
    );
};

export default BarcodeSearchModeContent;

const BarcodeListWrapper = styled(Flex)`
    width: 100%;
`;

const BarcodeContentWrapper = styled(Flex)`
    width: 100%;
`;

const StatusMessageRow = styled(Flex)`
    margin-top: 1rem;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.error};
    border-radius: var(--radius);
    background-color: ${({ theme }) => theme.colors.error}30;
    padding: 1rem 0.9rem;
`;

const BarcodeListItem = styled(Flex)`
    width: 100%;
    padding: 0.75rem;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: var(--radius);
    background-color: ${({ theme }) => theme.colors.fillLight};
`;

const AddedBadge = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.fillWhite};
    border: 1px solid ${({ theme }) => theme.colors.success};
    border-radius: 999px;
    padding: 0.2rem 0.5rem;
    background-color: ${({ theme }) => theme.colors.success};
`;
